import { getLocales } from 'assets/locales/Locale';
import { defaultDateTimeFormat, defaultServerDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { combineStrings } from 'assets/utils/data/String';
import dayjs from 'dayjs';
import Application from 'models/core/application/Model.Application';
import MediaFile from 'models/core/mediaFile/Model.MediaFile';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import Model from 'models/Model';
import Contract from '../contract/Model.Contract';
import statementDeleteApi from './delete/Api.Statement.Delete';
import statementListApi from './list/Api.Statement.List';
import statementSaveApi from './save/Api.Statement.Save';

export default class Statement
  extends Model<Model.IStatement>({ icon: 'fas-cloud' })
  implements Model.Instance<Model.IStatement>
{
  id: number;
  status: Model.Enum.StatementStatusCode;
  statementNumber: number;
  totalAmountDueCAD: number;
  invoicingDate: string;
  fromDate: string;
  toDate: string;
  createdAtUtc: string;
  createdAtLocal: string;
  modifiedAtUtc: string;
  reconciledAtUtc?: string;
  paidOnLocalDate?: string;
  representativeId: number;
  representative: User;
  dealerId: number;
  dealer: Dealer;
  contracts: Contract[];
  isEmailSent?: boolean;
  isPayed?: boolean;
  createdById?: number;
  createdBy?: User;
  modifiedById?: number;
  modifiedBy?: User;
  reconciledById?: number;
  reconciledBy?: User;
  applicationId?: number;
  application?: Application;
  mediaFiles?: MediaFile[];
  cancellationPdf?: MediaFile;

  constructor(data: Partial<Model.IStatement> | Utils.FormData<Model.IStatement>, language?: Locale.Languages) {
    super(
      {
        representative: User,
        dealer: Dealer,
        createdBy: User,
        modifiedBy: User,
        reconciledBy: User,
        application: Application,
        contracts: [Contract],
        mediaFiles: [MediaFile],
        cancellationPdf: MediaFile,
      },
      language
    );
    this.update(data);
  }
  get displayInfo(): Model.DisplayInfo {
    const { lang } = getLocales();
    const fromDate = dayjs(this.fromDate, defaultServerDateTimeFormat);
    const toDate = dayjs(this.toDate, defaultServerDateTimeFormat);
    const comparisonDate = dayjs('01/01/1901');

    const info =
      fromDate.isAfter(comparisonDate) && toDate.isAfter(comparisonDate)
        ? combineStrings(
            ' | ',
            lang[this.status],
            combineStrings(
              ' - ',
              localizeDate(fromDate, defaultDateTimeFormat),
              localizeDate(toDate, defaultDateTimeFormat)
            )
          )
        : combineStrings(' | ', lang[this.status], localizeDate(this.invoicingDate, 'MMMM YYYY'));

    return {
      id: this.id,
      title: this.dealer?.name || this.id.toString(),
      subtitle: this.contracts.map((it) => it.appNumberWithRevision).join(', '),
      info,
    };
  }

  static async list(body?: Api.Core.Statement.List.IRequest) {
    const { payload, ...rest } = await statementListApi(body);
    return {
      ...rest,
      payload: payload && {
        data: payload.data?.map((it) => new Statement(it)),
        count: payload.count,
      },
    };
  }
  async save() {
    return await statementSaveApi(this);
  }
  async delete() {
    return await statementDeleteApi(this.id);
  }
}
